import { useState } from "react";
import { Link } from "react-router-dom";
import { useSubscription, useUpgradeSubscription, useWalletBalance, useSubModels, useSubModelDetail, useUsdRate } from "../hooks/useApi";
import Card from "../components/Card";
import Button from "../components/Button";
import GlassModal from "../components/GlassModal";
import DepositModal from "../components/DepositModal";
import Badge from "../components/Badge";
import CopyButton from "../components/CopyButton";
import { formatToman, formatTomanInt, formatNumber, toPersianDigits } from "../utils/format";

interface PlanInfo {
  plan_id: number;
  name: string;
  plan_kind?: string; // "pay_as_you_go" (OpenRouter) | "fixed" (CodeCraft)
  description: string;
  monthly_price: number;
  fixed_price_usd?: number | string | null;
  priority_level: number;
  available_durations: number[];
  duration_discounts?: Record<string, number>;
  caching_discount: number;
  platform_fee: number;
  features: string[];
  // Fixed-cost (CodeCraftAPI) quota caps; null/ignored for pay-as-you-go.
  requests_per_minute?: number | null;
  tokens_per_minute?: number | null;
  monthly_token_limit?: number | null;
  max_concurrent_requests?: number | null;
  fixed_models?: string[] | null;
}

interface CurrentSubscription {
  subscription_id: number;
  plan_id?: number | null;
  plan_name: string;
  priority_level: number;
  start_date: string;
  end_date?: string | null;
  is_active: boolean;
}

interface SubscriptionData {
  current: CurrentSubscription | null;
  plans: PlanInfo[];
}

const DURATION_LABELS: Record<number, string> = {
  30: "۱ ماهه",
  90: "۳ ماهه",
  180: "۶ ماهه",
  365: "۱۲ ماهه",
};

// Accent colors mapped to the design-system status vars so cards adapt to light/dark.
const PLAN_ACCENTS = [
  { iconBg: "rgb(var(--status-success-bg))", iconText: "rgb(var(--status-success-text))" },
  { iconBg: "rgb(var(--status-info-bg))", iconText: "rgb(var(--status-info-text))" },
  { iconBg: "rgb(var(--status-warning-bg))", iconText: "rgb(var(--status-warning-text))" },
  { iconBg: "rgb(var(--status-error-bg))", iconText: "rgb(var(--status-error-text))" },
];

const PLAN_ICONS = [
  <svg key="a" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>,
  <svg key="b" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3v2M15 3v2M9 19v2M15 19v2" />
    <rect x="3" y="7" width="18" height="10" rx="2" />
    <path d="M7 10h2M15 10h2" />
  </svg>,
  <svg key="c" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 2 7l10 5 10-5-10-5z" />
    <path d="m2 17 10 5 10-5" />
    <path d="m2 12 10 5 10-5" />
  </svg>,
  <svg key="d" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>,
  <svg key="e" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12l4 6-10 13L2 9z" />
    <path d="M11 3 8 9l4 13 4-13-3-6" />
    <path d="M2 9h20" />
  </svg>,
];

const durationLabel = (days: number) =>
  DURATION_LABELS[days] || `${toPersianDigits(String(days))} روزه`;

const displayName = (name: string) => (name === "free" ? "رایگان" : toPersianDigits(name));

const discountFor = (plan: PlanInfo, duration: number) =>
  Math.max(0, Number(plan.duration_discounts?.[String(duration)] || 0));

const priceFor = (plan: PlanInfo, duration: number, original = false) => {
  const months = Math.max(1, Math.round(duration / 30));
  const base = Number(plan.monthly_price) * months;
  const discount = discountFor(plan, duration);
  if (original || discount <= 0) return base;
  if (plan.plan_kind === "fixed") return Number((base * (100 - discount) / 100).toFixed(2));
  return Math.round((base * (100 - discount)) / 100);
};

const planAccent = (plans: PlanInfo[], planId: number) => {
  const idx = Math.max(0, plans.findIndex((p) => p.plan_id === planId)) % PLAN_ACCENTS.length;
  return { accent: PLAN_ACCENTS[idx], icon: PLAN_ICONS[idx % PLAN_ICONS.length] };
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary shrink-0 mt-0.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Fixed-rate (sub) catalogue — capability master list shown as tick/cross.
const MODEL_CAPABILITIES: string[] = [
  "chat", "reasoning", "vision", "tools", "streaming",
  "function_calling", "json_mode", "vision_input", "audio_input",
  "audio_output", "file_input", "multilingual", "long_context", "code",
];

const CAPABILITY_LABELS: Record<string, string> = {
  chat: "چت", reasoning: "استدلال", vision: "بینایی", tools: "ابزارها (Tools)",
  streaming: "استریم کردن", function_calling: "فراخوانی توابع", json_mode: "خروجی JSON",
  vision_input: "ورودی تصویر", audio_input: "ورودی صدا", audio_output: "خروجی صدا",
  file_input: "ورودی فایل", multilingual: "چندزبانه", long_context: "پنجره طولانی", code: "تولید کد",
};

const MODALITY_FA: Record<string, string> = {
  text: "متن", image: "تصویر", audio: "صدا", video: "ویدئو",
  file: "فایل", embedding: "امبدینگ", code: "کد",
};

const modLabel = (v: unknown): string => {
  const s = String(v).toLowerCase();
  return MODALITY_FA[s] || s;
};

// "متن", "متن + تصویر", … — same Persian labeling as the /models drawer.
// Tolerates strings, arrays, and flag objects like {"text": true}.
const fmtModals = (vals: unknown): string => {
  if (vals === null || vals === undefined || vals === "") return "—";
  const flat: string[] = [];
  const collect = (v: unknown) => {
    if (v === null || v === undefined || v === "") return;
    if (Array.isArray(v)) {
      v.forEach(collect);
      return;
    }
    if (typeof v === "object") {
      Object.entries(v).forEach(([k, on]) => {
        if (on) flat.push(modLabel(k));
      });
      return;
    }
    flat.push(modLabel(v));
  };
  collect(vals);
  return flat.length ? flat.join(" + ") : "—";
};

// Flatten arbitrary JSON to a readable string — never "…Object]", so a
// structured `architecture` object renders as "input: text, output: text".
const strVal = (v: unknown): string => {
  const flat: string[] = [];
  const collect = (x: unknown) => {
    if (x === null || x === undefined || x === "") return;
    if (Array.isArray(x)) {
      x.forEach(collect);
      return;
    }
    if (typeof x === "object") {
      Object.entries(x).forEach(([k, on]) => {
        if (typeof on === "boolean") {
          if (on) flat.push(k);
        } else {
          flat.push(`${k}: ${strVal(on)}`);
        }
      });
      return;
    }
    flat.push(String(x));
  };
  collect(v);
  return flat.join(", ");
};

// The /models drawer renders معماری as "متن ← متن" plus the raw string
// (e.g. "text->text"). CodeCraft models may publish flag objects or a raw
// "text->text" string, so derive both sides defensively.
const archSides = (raw: Record<string, unknown>): [string, string] => {
  const input = raw.input_modalities;
  const output = raw.output_modalities;
  if ((input != null && input !== "") || (output != null && output !== "")) {
    return [fmtModals(input), fmtModals(output)];
  }
  const arch = raw.architecture ?? raw.architecture_modality;
  if (arch != null && arch !== "") {
    if (typeof arch === "object" && !Array.isArray(arch)) {
      const archObj = arch as Record<string, unknown>;
      const inSide = archObj["input"] ?? archObj["input_modalities"] ?? archObj["in"];
      const outSide = archObj["output"] ?? archObj["output_modalities"] ?? archObj["out"];
      if (inSide != null || outSide != null) {
        return [fmtModals(inSide), fmtModals(outSide)];
      }
    }
    const parts = strVal(
      typeof arch === "object" ? (arch as Record<string, unknown>)["modality"] || arch : arch
    ).split(/->|→/).map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) return [fmtModals([parts[0]]), fmtModals([parts[1]])];
    return [fmtModals([parts[0]]) || "—", "—"];
  }
  return ["—", "—"];
};

// Raw architecture string for the mono line. CodeCraft publishes it as an
// object like {"modality": "text+image->text", "input_modalities": [...],
// "tokenizer": "Other", ...}; show only the `modality` string, never the
// flattened metadata.
const archMono = (raw: Record<string, unknown>): string => {
  const v = raw.architecture ?? raw.architecture_modality;
  if (typeof v === "string") return v || "—";
  if (v && typeof v === "object") {
    const mod = (v as Record<string, unknown>)["modality"];
    if (typeof mod === "string" && mod) return mod;
  }
  const s = strVal(v);
  return s ? s : "—";
};

const paramItems = (raw: Record<string, unknown>): string[] => {
  const sp = raw.supported_parameters;
  if (Array.isArray(sp)) return sp.map(String);
  if (sp && typeof sp === "object") return Object.keys(sp);
  return [];
};

const SAMPLE_CODES: Record<"python" | "typescript" | "curl", string> = {
  python: `from openai import OpenAI

client = OpenAI(
    base_url="https://app.asha-ai.ir/sub/v1",
    api_key="YOUR_ASHA_API_KEY",
)

response = client.chat.completions.create(
    model="claude-fable-5",
    messages=[{"role": "user", "content": "سلام"}],
    stream=True,
)
for chunk in response:
    print(chunk.choices[0].delta.content or "", end="")
`,
  typescript: `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "https://app.asha-ai.ir/sub/v1",
  apiKey: "YOUR_ASHA_API_KEY",
});

const stream = await client.chat.completions.create({
  model: "claude-fable-5",
  messages: [{ role: "user", content: "سلام" }],
  stream: true,
});

for await (const chunk of stream) {
  const content = chunk.choices[0]?.delta?.content || "";
  process.stdout.write(content);
}
`,
  curl: `curl https://app.asha-ai.ir/sub/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ASHA_API_KEY" \\
  -d '{
    "model": "claude-fable-5",
    "messages": [{"role": "user", "content": "سلام"}],
    "stream": true
  }'
`,
};

const planFeatures = (plan: PlanInfo): string[] =>
  [
    ...plan.features.filter((f) => !f.startsWith("تخفیف کش")).map(toPersianDigits),
    Number(plan.caching_discount) > 0
      ? `سقف تخفیف سشن‌های طولانی و تسک‌های ایجنت‌محور: ٪${toPersianDigits(String(Number(plan.caching_discount)))}`
      : null,
    `کارمزد پلتفرم: ٪${toPersianDigits(String(Number(plan.platform_fee) || 0))}`,
    `اولویت پردازش: سطح ${toPersianDigits(String(plan.priority_level || 0))}`,
    "بدون محدودیت ارسال درخواست",
  ].filter(Boolean) as string[];

const FeatureList = ({ items }: { items: string[] }) => (
  <ul className="space-y-2 text-small text-text-secondary">
    {items.map((f) => (
      <li key={f} className="flex items-start gap-2">
        <CheckIcon />
        {f}
      </li>
    ))}
  </ul>
);

export default function Subscription() {
  const { data, isLoading } = useSubscription();
  const { data: wallet } = useWalletBalance();
  const upgrade = useUpgradeSubscription();
  const { data: usdRate } = useUsdRate();

  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(0);
  const [showInsufficient, setShowInsufficient] = useState(false);
  const [chargeAmount, setChargeAmount] = useState<number | null>(null);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [capacityError, setCapacityError] = useState(false);
  const [kindTab, setKindTab] = useState<"pay_as_you_go" | "fixed">("pay_as_you_go");
  const [sampleTab, setSampleTab] = useState<"python" | "typescript" | "curl">("python");
  const [modelsPlan, setModelsPlan] = useState<PlanInfo | null>(null);
  const [modelDetail, setModelDetail] = useState<Record<string, unknown> | null>(null);

  const { data: subModels, error: subModelsError, isPending: subModelsLoading } = useSubModels();

  const subDetailName = modelDetail ? String(modelDetail.model_name) : null;
  const { data: subModelDetail, isPending: subDetailLoading } = useSubModelDetail(subDetailName);
  // The detail endpoint publishes fields the list omits (supported_parameters,
  // architecture, modalities); fall back to the list row until it loads.
  const detail: Record<string, unknown> =
    (subModelDetail as Record<string, unknown> | undefined) || modelDetail || {};

  const current = data?.current ?? null;
  const currentFixed = data?.current_fixed ?? null;
  // Good-sorting by price so cards always read cheapest → most expensive and
  // the accent icons follow the same (price-specific) order across tabs.
  const plans: PlanInfo[] = [...(data?.plans || [])].sort(
    (a, b) => Number(a.monthly_price) - Number(b.monthly_price)
  );

  const walletBalance = Number(wallet?.balance || 0);

  const paygPlans = plans.filter((p) => p.plan_kind !== "fixed");
  const fixedPlans = plans.filter((p) => p.plan_kind === "fixed");
  const hasTabs = paygPlans.length > 0 && fixedPlans.length > 0;
  const activePlans =
    hasTabs
      ? (kindTab === "fixed" ? fixedPlans : paygPlans)
      : (fixedPlans.length > 0 ? fixedPlans : paygPlans);
  const showFixedExtras = kindTab === "fixed" || (!hasTabs && fixedPlans.length > 0);

  const selected = plans.find((p) => p.plan_id === selectedPlanId) || null;
  const totalPrice = selected && selectedDuration ? priceFor(selected, selectedDuration) : 0;
  const fixedUsdPrice = selected?.plan_kind === "fixed" && selectedDuration
    ? priceFor(selected, selectedDuration)
    : 0;
  const fixedIrrPrice = Math.ceil(fixedUsdPrice * Number(usdRate?.usd_rate_irr || 0));
  const payablePrice = selected?.plan_kind === "fixed" ? fixedIrrPrice : totalPrice;

  const isCurrentPlan = (plan: PlanInfo) =>
    plan.plan_kind === "fixed"
      ? currentFixed?.plan_id === plan.plan_id
      : current?.plan_id === plan.plan_id;

  const planCanRenew = (plan: PlanInfo) =>
    plan.plan_kind === "fixed" ? fixedCanRenew : canRenew;

  const daysToExpiry = current?.end_date
    ? Math.ceil((new Date(current.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const canRenew = daysToExpiry !== null && daysToExpiry < 7;

  const fixedDaysToExpiry = currentFixed?.end_date
    ? Math.ceil((new Date(currentFixed.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const fixedCanRenew = fixedDaysToExpiry !== null && fixedDaysToExpiry < 7;

  // Pre-fill the deposit modal with the full plan price for the selected
  // duration; the user can still edit the amount before paying.
  const openInsufficientModal = (price: number) => {
    setChargeAmount(price);
    closePlanModal();
    setShowInsufficient(true);
  };

  const openPlanModal = (plan: PlanInfo) => {
    setSelectedPlanId(plan.plan_id);
    setSelectedDuration(plan.available_durations[0] || 30);
  };

  const closePlanModal = () => {
    setSelectedPlanId(null);
    setSelectedDuration(0);
  };

  const handlePay = () => {
    if (!selected || !selectedDuration) return;
    if (payablePrice > walletBalance) {
      openInsufficientModal(payablePrice);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmPay = () => {
    if (!selected || !selectedDuration) return;
    setCapacityError(false);
    upgrade.mutate(
      { plan_id: selected.plan_id, duration: selectedDuration },
      {
        onSuccess: () => {
          setShowConfirm(false);
          closePlanModal();
        },
        onError: (err: unknown) => {
          const resp = (err as Record<string, unknown>)?.response as Record<string, unknown> | undefined;
          const payload = resp?.data as Record<string, unknown> | undefined;
          const detailRaw = payload?.detail;
          if (
            resp?.status === 409 &&
            (detailRaw === "در حال حاضر برای این اشتراک ظرفیت خالی وجود ندارد" ||
              detailRaw === "sub_plan_capacity_unavailable" ||
              (payload?.code === "sub_plan_capacity_unavailable"))
          ) {
            setCapacityError(true);
            return;
          }
          const detailStr = typeof detailRaw === "string" ? detailRaw : Array.isArray(detailRaw) ? String((detailRaw[0] as Record<string, unknown>)?.msg || "") : String(detailRaw || "");
          if (detailStr.toLowerCase().includes("insufficient")) {
            setShowConfirm(false);
            openInsufficientModal(payablePrice);
          }
        },
      }
    );
  };

  const COD = {
    bg: "rgba(16,185,129,0.10)",
    border: "rgba(16,185,129,0.35)",
    strong: "#047857",
    accent: "#10b981",
  };

  const renderPaygCard = (plan: PlanInfo) => {
    const { accent, icon } = planAccent(plans, plan.plan_id);
    const isCurrent = isCurrentPlan(plan);
    const isFree = Number(plan.monthly_price) === 0;
    const hasDurations = plan.available_durations.length > 0;

    const bodyItems = planFeatures(plan);

    const mobileAction =
      !isCurrent || planCanRenew(plan) ? (
        <Button
          size="sm"
          variant={isCurrent ? "secondary" : "primary"}
          onClick={() => openPlanModal(plan)}
          disabled={!hasDurations}
        >
          {isCurrent ? "تمدید اشتراک" : "انتخاب پلن"}
        </Button>
      ) : null;

    const desktopAction = isCurrent ? (
      planCanRenew(plan) ? (
        <Button variant="secondary" className="w-full" onClick={() => openPlanModal(plan)}>
          تمدید اشتراک
        </Button>
      ) : (
        <div className="w-full py-2.5 rounded-btn border border-line bg-surface-hover text-center text-small font-medium text-text-tertiary">
          اشتراک فعلی
        </div>
      )
    ) : (
      <Button className="w-full" onClick={() => openPlanModal(plan)} disabled={!hasDurations}>
        انتخاب پلن
      </Button>
    );

    return (
      <Card
        key={plan.plan_id}
        className={`relative flex flex-col overflow-hidden ${isCurrent ? "ring-2 ring-primary" : "ring-1 ring-line"}`}
      >
        {isCurrent && (
          <Badge variant="success" className="absolute top-4 left-4 right-auto z-10">اشتراک فعلی</Badge>
        )}

        {/* Header: icon, name, price, durations */}
        <div
          className="p-6 pb-4 border-b border-line"
          style={{ background: "rgb(var(--bg-surface-muted))" }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: accent.iconBg, color: accent.iconText }}
              >
                {icon}
              </div>
              <div>
                <h3 className="text-h3 font-semibold text-text-main">{displayName(plan.name)}</h3>
                {plan.description && <p className="text-small text-text-tertiary mt-0.5">{plan.description}</p>}
              </div>
            </div>
            {/* Mobile action button lives in the header */}
            <div className="md:hidden shrink-0">{mobileAction}</div>
          </div>

          <p className="text-2xl font-bold text-text-main font-number text-left mt-4 mb-1">
            {isFree ? "رایگان" : plan.plan_kind === "fixed" ? `$${Number(plan.fixed_price_usd ?? plan.monthly_price).toFixed(2)}` : formatToman(plan.monthly_price)}
            {!isFree && (
              <span className="text-small text-text-tertiary font-normal mr-1">{plan.plan_kind === "fixed" ? "USD/ماه" : "تومان/ماه"}</span>
            )}
          </p>

          {/* Durations + discounts: one row per duration with a dotted leader */}
          {hasDurations && (
            <div className="mt-3 space-y-1.5">
              {plan.available_durations.map((d) => {
                const discount = discountFor(plan, d);
                return (
                  <div key={d} className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-md text-[11px] bg-surface-hover text-text-secondary ring-1 ring-line shrink-0">
                      {durationLabel(d)}
                    </span>
                    <span className="flex-1 border-b-2 border-dotted border-text-tertiary/60 min-w-4" aria-hidden />
                    {discount > 0 ? (
                      <span
                        className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold shrink-0"
                        style={{ background: "rgb(var(--status-error-bg))", color: "rgb(var(--status-error-text))" }}
                      >
                        ٪{toPersianDigits(String(discount))} تخفیف
                      </span>
                    ) : (
                      <span className="text-[10px] text-text-tertiary shrink-0">بدون تخفیف</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Body: details + desktop action */}
        <div className="p-6 pt-4 flex flex-col flex-1">
          <div className="mb-5 flex-1">
            <FeatureList items={bodyItems} />
          </div>

          {/* Desktop action button lives at the end */}
          <div className="hidden md:block">{desktopAction}</div>
        </div>
      </Card>
    );
  };

  const renderFixedCard = (plan: PlanInfo) => {
    const isCurrent = isCurrentPlan(plan);
    const hasDurations = plan.available_durations.length > 0;
    const models = (plan.fixed_models as string[] | undefined) || [];
    const SHOWN = 4;
    // Same price-specific accent icon as the pay-as-you-go cards.
    const { accent, icon } = planAccent(plans, plan.plan_id);

    const mobileAction =
      !isCurrent || planCanRenew(plan) ? (
        <Button
          size="sm"
          variant={isCurrent ? "secondary" : "primary"}
          onClick={() => openPlanModal(plan)}
          disabled={!hasDurations}
        >
          {isCurrent ? "تمدید اشتراک" : "انتخاب پلن"}
        </Button>
      ) : null;

    const desktopAction = isCurrent ? (
      planCanRenew(plan) ? (
        <Button variant="secondary" className="w-full" onClick={() => openPlanModal(plan)}>
          تمدید اشتراک
        </Button>
      ) : (
        <div className="w-full py-2.5 rounded-btn border border-line bg-surface-hover text-center text-small font-medium text-text-tertiary">
          اشتراک فعلی
        </div>
      )
    ) : (
      <Button className="w-full" onClick={() => openPlanModal(plan)} disabled={!hasDurations}>
        انتخاب پلن
      </Button>
    );

    return (
      <div
        key={plan.plan_id}
        className="relative flex flex-col overflow-hidden rounded-card"
        style={{
          background: "rgb(var(--bg-surface))",
          border: `1.5px solid ${COD.accent}`,
          boxShadow: "0px 1px 3px rgba(0,0,0,0.06), 0px 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        {isCurrent && (
          <Badge variant="success" className="absolute top-4 left-4 right-auto z-10">اشتراک فعلی</Badge>
        )}

        {/* Header: CodeCraft green, flat-rate style */}
        <div className="p-6 pb-4 border-b" style={{ background: COD.bg, borderColor: COD.border }}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: accent.iconBg, color: accent.iconText }}
              >
                {icon}
              </div>
              <div>
                <h3 className="text-h3 font-semibold text-text-main">{displayName(plan.name)}</h3>
                <span
                  className="inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold mt-1"
                  style={{ background: COD.accent, color: "#fff" }}
                >
                  تعرفه ثابت
                </span>
              </div>
            </div>
            {/* Mobile action button lives in the header */}
            <div className="md:hidden shrink-0">{mobileAction}</div>
          </div>

          <p className="text-2xl font-bold text-text-main font-number text-left mt-4 mb-1">
            ${Number(plan.monthly_price).toFixed(2)}
            <span className="text-small text-text-tertiary font-normal mr-1">USD/ماه</span>
          </p>

          {/* Durations + discounts: one row per duration with a dotted leader */}
          {hasDurations && (
            <div className="mt-3 space-y-1.5">
              {plan.available_durations.map((d) => {
                const discount = discountFor(plan, d);
                return (
                  <div key={d} className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-md text-[11px] bg-surface-hover text-text-secondary ring-1 ring-line shrink-0">
                      {durationLabel(d)}
                    </span>
                    <span className="flex-1 border-b-2 border-dotted border-text-tertiary/60 min-w-4" aria-hidden />
                    {discount > 0 ? (
                      <span
                        className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold shrink-0"
                        style={{ background: "rgb(var(--status-error-bg))", color: "rgb(var(--status-error-text))" }}
                      >
                        ٪{toPersianDigits(String(discount))} تخفیف
                      </span>
                    ) : (
                      <span className="text-[10px] text-text-tertiary shrink-0">بدون تخفیف</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Body: quota caps + included models + desktop action */}
        <div className="p-6 pt-4 flex flex-col flex-1">
          <div
            className="rounded-xl px-3 py-2.5 mb-4 space-y-2 text-small"
            style={{ background: COD.bg, border: `1px solid ${COD.border}` }}
          >
            <div className="flex items-center justify-between">
              <span className="text-text-tertiary">محدودیت نرخ — درخواست/دقیقه</span>
              <span className="font-semibold font-number" style={{ color: COD.strong }}>{formatNumber(plan.requests_per_minute)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-tertiary">محدودیت نرخ — توکن/دقیقه</span>
              <span className="font-semibold font-number" style={{ color: COD.strong }}>{formatNumber(plan.tokens_per_minute)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-tertiary">سقف توکن ماهانه</span>
              <span className="font-semibold font-number" style={{ color: COD.strong }}>{formatNumber(plan.monthly_token_limit)}</span>
            </div>
            {Number(plan.max_concurrent_requests || 0) > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-text-tertiary">درخواست همزمان</span>
                <span className="font-semibold font-number" style={{ color: COD.strong }}>{formatNumber(plan.max_concurrent_requests)}</span>
              </div>
            )}
          </div>

          {/* Included models */}
          <div className="mb-5 flex-1">
            {models.length === 0 ? (
              <p className="text-small" style={{ color: COD.strong }}>
                دسترسی به همه مدل‌های این سرویس
              </p>
            ) : (
              <>
                <p className="text-[11px] mb-1.5 text-text-tertiary">مدل‌های شامل</p>
                <div className="flex flex-wrap gap-1.5 text-[11px]">
                  {models.slice(0, SHOWN).map((m) => (
                    <span
                      key={m}
                      className="px-2 py-0.5 rounded-md truncate max-w-[120px]"
                      style={{ background: COD.bg, color: "rgb(var(--text-secondary))" }}
                      title={m}
                    >
                      {m}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-1.5 text-[11px] font-semibold underline cursor-pointer"
                  style={{ color: COD.accent }}
                  onClick={() => setModelsPlan(plan)}
                >
                  نمایش همه ({toPersianDigits(String(models.length))})
                </button>
              </>
            )}
          </div>

          {/* Desktop action button lives at the end */}
          <div className="hidden md:block">{desktopAction}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1 text-text-main">اشتراک</h1>
          <p className="text-body text-text-secondary mt-1">پلن‌ها و اشتراک شما</p>
        </div>
      </div>

      {/* Careful-selection hint */}
      <div
        className="rounded-card border p-4 flex items-start gap-3 text-small"
        style={{ background: "rgb(var(--bg-surface-muted))" }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="mt-0.5 shrink-0 text-primary">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <p className="flex-1 text-text-secondary">
          پلن‌ها را با دقت انتخاب کنید؛ اگر در تفاوت‌های بین آن‌ها مطمئن نیستید، می‌توانید از طریق{" "}
          <span className="font-medium text-text-main">ثبت تیکت</span> از تیم پشتیبانی بپرسید — در سریعترین زمان پاسخ شما را میدهیم.
        </p>
      </div>

      {/* Current plan */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 left-0 w-40 h-40 bg-primary/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <p className="text-small text-text-tertiary mb-1">اشتراک فعلی</p>
            {isLoading || !current ? (
              <div className="h-11 w-56 bg-surface-hover rounded animate-pulse" />
            ) : (
              <div className="flex items-center gap-3">
                <h2 className="text-h1 text-text-main">{displayName(current.plan_name)}</h2>
                {current.is_active ? (
                  canRenew ? (
                    <Badge variant="warning" className="!text-h3 !px-3 !py-1">در حال انقضا</Badge>
                  ) : (
                    <Badge variant="success" className="!text-h3 !px-3 !py-1">فعال</Badge>
                  )
                ) : (
                  <Badge variant="error" className="!text-h3 !px-3 !py-1">منقضی شده</Badge>
                )}
              </div>
            )}
            {current?.end_date && (
              <p className="text-small text-text-tertiary mt-2">
                تا {new Date(current.end_date).toLocaleDateString("fa-IR")}
                {daysToExpiry !== null && daysToExpiry >= 0 && (
                  <span className="mr-2">({daysToExpiry === 0 ? "امروز منقضی می‌شود" : `${toPersianDigits(String(daysToExpiry))} روز باقی`})</span>
                )}
              </p>
            )}
          </div>
          <div className="text-left flex flex-col items-end gap-3">
            <div>
              <p className="text-small text-text-tertiary mb-1">موجودی کیف پول</p>
              <p className="text-h2 font-semibold text-text-main font-number">
                {formatTomanInt(wallet?.balance)} <span className="text-small text-text-tertiary">تومان</span>
              </p>
            </div>
            {current && current.is_active && canRenew && current.plan_id != null && plans.some((p) => p.plan_id === current.plan_id) && (
              <Button size="sm" variant="secondary" onClick={() => openPlanModal(plans.find((p) => p.plan_id === current.plan_id) as PlanInfo)}>
                تمدید اشتراک
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Kind tabs */}
      {hasTabs && activePlans.length > 0 && (
        <div className="grid grid-cols-2 rounded-xl p-1 md:flex md:items-center md:gap-2.5 md:bg-transparent md:p-0 md:rounded-none">
          <button
            type="button"
            onClick={() => setKindTab("pay_as_you_go")}
            className={`px-1 md:px-6 py-2.5 rounded-lg text-small transition-colors ${
              kindTab === "pay_as_you_go"
                ? "bg-primary-light text-primary font-bold border-2 border-primary/40 shadow-sm"
                : "bg-surface-hover text-text-secondary border border-line hover:text-text-primary hover:border-primary/25"
            }`}
          >
            پلن‌های مصرفی
            <span className="mr-1.5 px-1.5 rounded-md text-[10px] bg-surface-hover">{toPersianDigits(String(paygPlans.length))}</span>
          </button>
          <button
            type="button"
            onClick={() => setKindTab("fixed")}
            className={`px-1 md:px-6 py-2.5 rounded-lg text-small transition-colors ${
              kindTab === "fixed"
                ? "bg-primary-light text-primary font-bold border-2 border-primary/40 shadow-sm"
                : "bg-surface-hover text-text-secondary border border-line hover:text-text-primary hover:border-primary/25"
            }`}
          >
            تعرفه ثابت
            <span
              className="mr-1.5 px-1.5 rounded-md text-[10px] font-semibold"
              style={{ background: "rgba(16,185,129,0.10)", color: "#10b981" }}
            >
              {toPersianDigits(String(fixedPlans.length))}
            </span>
          </button>
        </div>
      )}

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {activePlans.map((plan) =>
          plan.plan_kind === "fixed" ? renderFixedCard(plan) : renderPaygCard(plan)
        )}
      </div>

      {fixedPlans.length > 0 && (
        <p className="text-small text-text-tertiary">
          پلن‌های تعرفه ثابت شامل یک سقف مصرف ماهانه هستند؛ API این پلن‌ها در حال حاضر از اندپوینت مکالمه (chat/completions) پشتیبانی می‌کند.
        </p>
      )}

      {plans.length > 0 && (
        <p className="text-small text-text-tertiary">
          {`پلن‌های مصرفی از بیش از ۵۰۰ مدل و انواع اندپوینت‌ها برای هر یک پشتیبانی می‌کند: `}
          <Link to="/models" className="text-primary font-medium hover:underline">
            مشاهده صفحه مدل‌ها
          </Link>
        </p>
      )}

      {showFixedExtras && (
        <>
          {/* Sample request — tabbed languages + copy */}
          <div className="rounded-card border p-4 space-y-2.5" style={{ background: "rgb(var(--bg-surface-muted))" }}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-small text-text-tertiary">نمونه درخواست — API تعرفه ثابت:</p>
              <CopyButton text={SAMPLE_CODES[sampleTab]} label="کپی" className="!py-1 px-2 rounded-md bg-surface-hover border border-line" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(["python", "typescript", "curl"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setSampleTab(tab)}
                  className={`py-1.5 px-3 rounded-md text-[11px] font-medium transition-colors ${
                    sampleTab === tab
                      ? "bg-primary-light text-primary font-semibold"
                      : "bg-surface-hover text-text-secondary hover:text-text-primary"
                  }`}
                >
                  {tab === "python" ? "Python" : tab === "typescript" ? "TypeScript" : "cURL"}
                </button>
              ))}
            </div>
            <pre
              dir="ltr"
              className="rounded-xl overflow-x-auto p-3 text-[12px] leading-relaxed font-mono"
              style={{ background: "rgba(15,23,42,0.92)", color: "#e2e8f0", border: "1px solid rgba(16,185,129,0.35)" }}
            >{SAMPLE_CODES[sampleTab]}</pre>
            <p className="text-small text-text-tertiary">
              توجه: برای آیدی مدل درست، از فهرست زیر؛ کلید API نیز همان کلید Asha (بخش API Keys) است.
            </p>
          </div>

          {/* Model catalogue cards */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-small text-text-tertiary">
                مدل‌های تعرفه ثابت ({toPersianDigits(String((subModels as Record<string, unknown>[] | undefined)?.length || 0))})
              </p>
            </div>
            {subModelsLoading ? (
              <div className="flex items-center justify-center py-10">
                <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "rgb(var(--text-tertiary))" }}>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : subModelsError || !(subModels as Record<string, unknown>[] | undefined)?.length ? (
              <Card className="text-center py-10">
                <p className="text-body text-text-tertiary">دریافت فهرست مدل‌ها ناموفق شد؛ بعداً دوباره امتحان کنید</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {(subModels as Record<string, unknown>[]).map((m) => (
                  <button
                    key={m.model_name as string}
                    type="button"
                    className="rounded-card border p-4 flex flex-col items-start gap-2 text-left transition-colors hover:bg-surface-hover cursor-pointer"
                    style={{ textAlign: "left", background: "rgb(var(--bg-surface))" }}
                    onClick={() => setModelDetail(m)}
                  >
                    <div className="w-full flex items-center justify-between gap-2">
                      <span className="font-semibold text-small text-text-main">{m.display_name as string}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md" style={{ background: "rgba(16,185,129,0.10)", color: "#10b981" }}>
                        تعرفه ثابت
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0 w-full" dir="ltr">
                      <span className="truncate text-[11px] font-mono text-text-tertiary">{String(m.model_name)}</span>
                      <CopyButton text={String(m.model_name)} iconOnly />
                    </div>
                    {(m.description as string | undefined) ? (
                      <p className="text-[11px] text-text-tertiary line-clamp-2 text-left" dir="ltr">{m.description as string}</p>
                    ) : (
                      <p className="text-[11px] text-text-tertiary/60 line-clamp-2 italic text-right" dir="rtl">توضیح تکمیلی منتشر نشده — شناسه را مستقیماً در درخواست استفاده کنید.</p>
                    )}
                    <div className="flex items-center gap-1.5">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="text-text-tertiary">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="5" y1="21" x2="5" y2="4" />
                        <line x1="20" y1="4" x2="20" y2="21" />
                      </svg>
                      <span className="text-[11px] text-text-tertiary">
                        {(() => {
                          const cw = (m.context_window ?? (m as Record<string, unknown>).context_length) as unknown;
                          return cw == null || cw === "" || Number.isNaN(Number(cw)) ? "پنجره: —" : `پنجره: ${formatNumber(Number(cw))} توکن`;
                        })()}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {plans.length === 0 && !isLoading && (
        <Card className="text-center py-12">
          <p className="text-body text-text-tertiary">در حال حاضر پلنی برای نمایش وجود ندارد</p>
        </Card>
      )}

      {/* Plan purchase modal */}
      <GlassModal
        open={selectedPlanId !== null}
        onClose={closePlanModal}
        title={selected ? displayName(selected.name) : "انتخاب پلن"}
      >
        {selected && (
          <div className="space-y-5">
            {/* Plan summary: icon, name, description */}
            {(() => {
              const { accent, icon } = planAccent(plans, selected.plan_id);
              return (
                <div
                  className="flex items-center gap-3 rounded-btn p-4"
                  style={{ background: "rgb(var(--bg-surface-muted))" }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: accent.iconBg, color: accent.iconText }}
                  >
                    {icon}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-text-main">{displayName(selected.name)}</p>
                    {selected.description && (
                      <p className="text-small text-text-tertiary mt-0.5">{selected.description}</p>
                    )}
                  </div>
                  <div className="mr-auto text-left shrink-0">
                    <p className="text-h3 font-bold text-text-main font-number">
                      {Number(selected.monthly_price) === 0 ? "رایگان" : selected.plan_kind === "fixed" ? `$${Number(selected.monthly_price).toFixed(2)}` : formatToman(selected.monthly_price)}
                    </p>
                    {Number(selected.monthly_price) !== 0 && (
                      <p className="text-[10px] text-text-tertiary">{selected.plan_kind === "fixed" ? "USD/ماه" : "تومان/ماه"}</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Plan features (kind-aware) */}
            {selected.plan_kind === "fixed" ? (
              <>
                <div
                  className="rounded-xl px-3 py-2.5 space-y-2 text-small"
                  style={{ background: COD.bg, border: `1px solid ${COD.border}` }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-text-tertiary">محدودیت نرخ — درخواست/دقیقه</span>
                    <span className="font-semibold font-number" style={{ color: COD.strong }}>{formatNumber(selected.requests_per_minute)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-tertiary">محدودیت نرخ — توکن/دقیقه</span>
                    <span className="font-semibold font-number" style={{ color: COD.strong }}>{formatNumber(selected.tokens_per_minute)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-tertiary">سقف توکن ماهانه</span>
                    <span className="font-semibold font-number" style={{ color: COD.strong }}>{formatNumber(selected.monthly_token_limit)}</span>
                  </div>
                  {Number(selected.max_concurrent_requests || 0) > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-tertiary">درخواست همزمان</span>
                      <span className="font-semibold font-number" style={{ color: COD.strong }}>{formatNumber(selected.max_concurrent_requests)}</span>
                    </div>
                  )}
                </div>

                {(() => {
                  const mods = (selected.fixed_models as string[] | undefined) || [];
                  return mods.length === 0 ? (
                    <p className="text-small" style={{ color: COD.strong }}>دسترسی به همه مدل‌های این سرویس</p>
                  ) : (
                    <div>
                      <p className="text-[11px] mb-1.5 text-text-tertiary">مدل‌های شامل</p>
                      <div className="flex flex-wrap gap-1.5 text-[11px]">
                        {mods.slice(0, 6).map((m) => (
                          <span
                            key={m}
                            className="px-2 py-0.5 rounded-md truncate max-w-[120px]"
                            style={{ background: COD.bg, color: "rgb(var(--text-secondary))" }}
                            title={m}
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                      {mods.length > 6 && (
                        <button
                          type="button"
                          className="mt-1.5 text-[11px] font-semibold underline cursor-pointer"
                          style={{ color: COD.accent }}
                          onClick={() => setModelsPlan(selected)}
                        >
                          نمایش همه ({toPersianDigits(String(mods.length))})
                        </button>
                      )}
                    </div>
                  );
                })()}

                <div
                  className="rounded-xl px-3 py-2.5 text-small"
                  style={{ background: "rgb(var(--status-warning-bg))", border: "1px solid rgb(var(--status-warning-text))" }}
                >
                  <p className="flex items-start gap-2 text-status-warning-text">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5">
                      <path d="M12 9v4" />
                      <path d="M12 17h.01" />
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    </svg>
                    <span>
                      خریدن یک پلن تعرفه ثابت جدید، پلن ثابت قبلی شما را جایگزین می‌کند؛{" "}
                      <b>توکن‌های استفاده‌نشده دوره قبل از بین می‌رود</b> و شمارش توکن و تاریخ شروع/پایان دوباره از روز صفر حساب می‌شود.
                    </span>
                  </p>
                </div>
              </>
            ) : (
              <FeatureList items={planFeatures(selected)} />
            )}

            <div>
              <label className="block text-small font-medium text-text-secondary mb-1.5">مدت اشتراک</label>
              <div className="grid grid-cols-2 gap-2">
                {selected.available_durations.map((d) => {
                  const discount = discountFor(selected, d);
                  return (
                    <button
                      key={d}
                      onClick={() => setSelectedDuration(d)}
                      className={`relative py-2.5 rounded-btn border text-small transition-all ${
                        selectedDuration === d
                          ? "border-primary bg-primary-light text-primary font-semibold"
                          : "border-line text-text-secondary hover:bg-surface-hover"
                      }`}
                    >
                      {durationLabel(d)}
                      {discount > 0 && (
                        <span
                          className="absolute -top-2 left-2 px-1.5 py-0.5 rounded-md text-[10px] font-semibold"
                          style={{ background: "rgb(var(--status-error-bg))", color: "rgb(var(--status-error-text))" }}
                        >
                          ٪{toPersianDigits(String(discount))} تخفیف
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {(() => {
              const discount = selectedDuration ? discountFor(selected, selectedDuration) : 0;
              const original = selectedDuration ? priceFor(selected, selectedDuration, true) : 0;
              if (!selectedDuration || discount <= 0) return null;
              return (
                <div className="flex items-center justify-between rounded-btn bg-surface-hover px-4 py-2.5">
                  <span className="text-small text-text-secondary line-through font-number">
                    {selected.plan_kind === "fixed" ? `$${original.toFixed(2)}` : formatToman(original)}
                  </span>
                  <span className="text-small font-semibold" style={{ color: "rgb(var(--status-error-text))" }}>
                    سود شما: ٪{toPersianDigits(String(discount))} تخفیف
                  </span>
                </div>
              );
            })()}

            <div className="flex items-center justify-between rounded-btn bg-surface-hover px-4 py-3">
              <span className="text-small text-text-secondary">مبلغ قابل پرداخت</span>
              <div className="text-left">
                <span className="text-h3 font-semibold text-text-main font-number">
                    {selected.plan_kind === "fixed"
                      ? `$${fixedUsdPrice.toFixed(2)} · ${formatToman(fixedIrrPrice)} تومان`
                      : <>{formatToman(totalPrice)} <span className="text-small text-text-tertiary">تومان</span></>}
                </span>
                {selected.plan_kind === "fixed" && usdRate && (
                  <p className="text-[10px] text-text-tertiary mt-1">
                    هر دلار: {formatNumber(Number(usdRate.usd_rate_toman))} تومان
                  </p>
                )}
              </div>
            </div>

            <p className="text-small text-text-tertiary">
              موجودی کیف پول: {formatTomanInt(wallet?.balance)} تومان
            </p>

            <Button
              className="w-full !py-3"
              onClick={handlePay}
              loading={upgrade.isPending}
              disabled={!selectedDuration || (selected.plan_kind === "fixed" && !Number(usdRate?.usd_rate_irr))}
            >
              پرداخت از کیف پول
            </Button>
          </div>
        )}
      </GlassModal>

      {/* Included models (fixed-cost plans) modal */}
      <GlassModal
        open={modelsPlan !== null}
        onClose={() => setModelsPlan(null)}
        title={modelsPlan ? `مدل‌های ${displayName(modelsPlan.name)}` : "مدل‌های پلن"}
      >
        {modelsPlan && (
          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-btn bg-surface-hover px-4 py-3">
              <span className="text-small text-text-tertiary">کل مدل‌های این پلن</span>
              <span className="font-semibold font-number" style={{ color: "#047857" }}>
                {toPersianDigits(String((modelsPlan.fixed_models as string[] | undefined)?.length || 0))}
              </span>
            </div>

            {((modelsPlan.fixed_models as string[] | undefined) || []).length === 0 ? (
              <p className="text-body text-text-secondary leading-7">
                این پلن به <span className="font-semibold" style={{ color: "#10b981" }}>همه مدل‌های این سرویس</span> دسترسی دارد.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(modelsPlan.fixed_models as string[]).map((m) => (
                  <span
                    key={m}
                    className="px-3 py-1.5 rounded-lg text-[11px] font-medium"
                    style={{ background: "rgba(16,185,129,0.10)", color: "rgb(var(--text-secondary))" }}
                    dir="ltr"
                  >
                    {m}
                  </span>
                ))}
              </div>
            )}

            <Button className="w-full !py-3" variant="secondary" onClick={() => setModelsPlan(null)}>
              بستن
            </Button>
          </div>
        )}
      </GlassModal>

      {/* Model detail — slide-in drawer (same half-screen slide as /models) */}
      {modelDetail && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-fade-in"
            onClick={() => setModelDetail(null)}
          />
          <aside className="absolute top-0 bottom-0 left-0 w-full md:w-1/2 bg-surface-base border-r border-line shadow-[0_20px_60px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden animate-drawer-left">
            {/* Header */}
            <div className="bg-surface px-6 pt-5 pb-4 border-b border-line">
              <div className="flex items-start justify-between gap-4" dir="ltr">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-medium text-primary bg-primary-light rounded px-1.5 py-0.5">تعرفه ثابت</span>
                  </div>
                  <h2 className="text-h2 text-text-main truncate">{String(detail.display_name || "—")}</h2>
                  <div className="flex items-center gap-1 mt-1.5">
                    <code className="font-mono text-small text-text-tertiary ltr block text-left tracking-tight min-w-0 truncate">
                      {String(detail.model_name)}
                    </code>
                    <CopyButton
                      text={String(detail.model_name)}
                      iconOnly
                      title="کپی نام مدل"
                    />
                  </div>
                </div>
                <button
                  onClick={() => setModelDetail(null)}
                  className="text-text-tertiary hover:text-text-main transition-colors p-1 shrink-0"
                  aria-label="بستن"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {(() => {
                const desc = detail.description;
                if (!desc) {
                  return (
                    <p className="text-body text-text-tertiary leading-relaxed italic text-right" dir="rtl">
                      توضیح تکمیلی این مدل توسط کاتالوگ منتشر نشده — شناسه مدل را مستقیماً در درخواست (`model`) استفاده کنید. اندپوینت: ‎/sub/v1/chat/completions (سازگار با OpenAI، استریم فعال).
                    </p>
                  );
                }
                return (
                  <p className="text-body text-text-secondary leading-relaxed text-left" dir="ltr">
                    {String(desc)}
                  </p>
                );
              })()}

              {subDetailLoading && !subModelDetail && (
                <div className="flex items-center justify-center py-10">
                  <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}

              {!subDetailLoading && (
                <>
                  <div>
                    <h4 className="text-h3 font-bold text-text-main">مشخصات</h4>
                    <dl className="divide-y divide-line">
                      <div className="flex items-start justify-between gap-4 py-2.5 border-b border-line last:border-0">
                        <span className="text-small text-text-secondary shrink-0">معماری</span>
                        <span className="min-w-0 text-right">
                          <span className="block text-body text-text-main">
                            {(() => { const [inSide, outSide] = archSides(detail); return (<>{inSide} <span className="text-text-tertiary mx-0.5">←</span> {outSide}</>); })()}
                          </span>
                          <span className="block font-mono text-small text-text-tertiary mt-1 text-left" dir="ltr">
                            {archMono(detail)}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-4 py-2.5 border-b border-line last:border-0">
                        <span className="text-small text-text-secondary shrink-0">حافظه (context)</span>
                        <span className="font-mono min-w-0 text-body text-text-main break-words" dir="rtl">
                          {(() => {
                            const cw = (detail.context_window ?? (detail as Record<string, unknown>).context_length) as unknown;
                            return cw != null && cw !== "" && !Number.isNaN(Number(cw)) ? `${formatNumber(Number(cw))} توکن` : "—";
                          })()}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-4 py-2.5 border-b border-line last:border-0">
                        <span className="text-small text-text-secondary shrink-0">نوع</span>
                        <span className="font-mono min-w-0 text-body text-text-main break-words" dir="rtl">
                          {String(detail.type ?? "chat")}
                        </span>
                      </div>
                    </dl>
                  </div>

                  {(() => {
                    const params = paramItems(detail);
                    if (!params.length) return null;
                    return (
                      <div>
                        <h4 className="text-h3 font-bold text-text-main">پارامترهای قابل تنظیم</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {params.map((p) => (
                            <code
                              key={p}
                              dir="ltr"
                              className="font-mono text-mono text-text-secondary bg-surface-hover border border-line rounded px-2 py-0.5"
                            >
                              {p}
                            </code>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  <div>
                    <h4 className="text-h3 font-bold text-text-main">قابلیت‌های پشتیبانی‌شده</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(() => {
                        const caps = ((detail.capabilities as unknown[] | undefined) || []).map(String).map((c) => c.toLowerCase());
                        const allCaps = Array.from(new Set(MODEL_CAPABILITIES.map((c) => c.toLowerCase()).concat(caps)));
                        return allCaps.map((cap) => {
                          const on = caps.includes(cap);
                          return on ? (
                            <span
                              key={cap}
                              className="flex items-center gap-1.5 text-small text-primary bg-status-success-bg border border-status-success-text/20 rounded-btn px-2.5 py-1.5"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
                                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              {CAPABILITY_LABELS[cap] || cap}
                            </span>
                          ) : (
                            <span
                              key={cap}
                              className="flex items-center gap-1.5 text-small text-text-tertiary bg-surface-hover border border-line rounded-btn px-2.5 py-1.5"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
                                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                              </svg>
                              {CAPABILITY_LABELS[cap] || cap}
                            </span>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-h3 font-bold text-text-main">پایان‌پوینت‌های پشتیبانی‌شده</h4>
                    <div className="flex flex-wrap gap-2">
                      <code
                        dir="ltr"
                        className="flex items-center gap-2 font-mono text-mono text-text-main bg-surface-hover border border-line rounded-btn px-3 py-1.5"
                      >
                        <span className="text-[10px] font-medium text-primary bg-primary-light rounded px-1 py-0.5">POST</span>
                        /sub/v1/chat/completions
                      </code>
                    </div>
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* Confirm payment modal */}
      <GlassModal
        open={showConfirm}
        onClose={() => { if (!upgrade.isPending) setShowConfirm(false); }}
        title="تأیید پرداخت"
      >
        <div className="space-y-5">
          <div className="flex flex-col items-center text-center gap-3 py-2">
            <div className="w-14 h-14 rounded-full flex items-center justify-center bg-primary/10 text-primary">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            {selected && (
              <p className="text-body text-text-secondary leading-7">
                آیا مطمئن هستید می‌خواهید{" "}
                <span className="font-semibold text-text-main">{selected.plan_kind === "fixed" ? `$${fixedUsdPrice.toFixed(2)} (${formatToman(fixedIrrPrice)} تومان)` : `${formatToman(totalPrice)} تومان`}</span>{" "}از کیف پول شما کسر شود و اشتراکتان به پلن{" "}
                <span className="font-semibold text-text-main">{displayName(selected.name)}</span> به مدت{" "}
                <span className="font-semibold text-text-main">{durationLabel(selectedDuration)}</span> ارتقا یابد؟
              </p>
            )}
          </div>

          {selected && (
            <div className="bg-surface-hover rounded-btn p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-small text-text-tertiary">پلن</span>
                <span className="text-body font-medium text-text-main">{selected && displayName(selected.name)} — {durationLabel(selectedDuration)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-small text-text-tertiary">مبلغ قابل پرداخت</span>
                <span className="text-body font-semibold font-number text-text-main">{selected.plan_kind === "fixed" ? `$${fixedUsdPrice.toFixed(2)} · ${formatToman(fixedIrrPrice)} تومان` : `${formatToman(totalPrice)} تومان`}</span>
              </div>
              <div className="border-t border-line" />
              <div className="flex items-center justify-between">
                <span className="text-small text-text-tertiary">موجودی فعلی</span>
                <span className="text-small font-number text-text-main">{formatTomanInt(wallet?.balance)} تومان</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-small text-text-tertiary">موجودی پس از پرداخت</span>
                <span className="text-small font-number font-semibold text-primary">{formatTomanInt(Math.max(0, walletBalance - payablePrice))} تومان</span>
              </div>
            </div>
          )}

          {capacityError && (
            <div
              role="alert"
              className="rounded-btn border px-4 py-3 text-small font-medium"
              style={{
                color: "rgb(var(--status-error-text))",
                background: "rgb(var(--status-error-bg))",
                borderColor: "rgb(var(--status-error-text) / 0.25)",
              }}
            >
              در حال حاضر برای این اشتراک ظرفیت خالی وجود ندارد
            </div>
          )}

          <div className="flex gap-3">
            <Button className="flex-1 !py-3" variant="secondary" onClick={() => setShowConfirm(false)} disabled={upgrade.isPending}>
              انصراف
            </Button>
            <Button className="flex-1 !py-3" onClick={handleConfirmPay} loading={upgrade.isPending}>
              تأیید و پرداخت
            </Button>
          </div>
        </div>
      </GlassModal>

      {/* Insufficient credit modal */}
      <GlassModal
        open={showInsufficient}
        onClose={() => setShowInsufficient(false)}
        title="موجودی کافی نیست"
      >
        <div className="space-y-5">
          <div className="flex flex-col items-center text-center gap-2 py-2">
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-rose-500 bg-rose-50">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            </div>
            <p className="text-body text-text-secondary">
              موجودی کیف پول شما برای خرید این پلن کافی نیست.
            </p>
            {chargeAmount != null && (
              <p className="text-small text-text-tertiary">
                حداقل <span className="font-number font-semibold text-text-main">{formatTomanInt(Math.max(0, chargeAmount - walletBalance))}</span> تومان باید شارژ کنید
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <Button className="flex-1 !py-3" variant="secondary" onClick={() => setShowInsufficient(false)}>
              بستن
            </Button>
            <Button
              className="flex-1 !py-3"
              onClick={() => {
                setShowInsufficient(false);
                setShowDeposit(true);
              }}
            >
              شارژ کیف پول
            </Button>
          </div>
        </div>
      </GlassModal>

      {/* Wallet deposit modal: presetAmount is in Toman, price is stored as IRR → divide by 10 */}
      <DepositModal
        open={showDeposit}
        onClose={() => setShowDeposit(false)}
        presetAmount={chargeAmount != null ? Math.round(chargeAmount / 10) : null}
      />
    </div>
  );
}
